/// <reference path='common.ts' />
/// <reference path='element_bind.ts' />

/**
 * テーブルに対する操作を行うクラスです。
 * tbodyタグの最終行をサンプルとして使用し、テーブル構築時に非表示にします。
 * HTMLのDOM構築完了時およびloadイベント発生時に自動的に内部のtableエレメントを管理対象とします。
 *
 * 属性
 * data-table-fetch: データ取得対象となるURL（この属性が付いているテーブルのみ操作対象とします）
 * data-table-param: データの検索条件（JSON形式）
 * data-table-bound: イベントを関連付けた際に付与される（外部からは変更しないでください）
 * data-table-busy: 処理中の場合に定義される（外部からは変更しないでください）
 * data-table-name-offset: データ開始番号を格納するパラメータ名（デフォルト値: offset）
 * data-table-name-limit: データの最大件数を格納するパラメータ名（デフォルト値: limit）
 * data-table-name-list: レスポンスのデータリストが格納されているパラメータ名（デフォルト値: list）
 * data-table-name-total: レスポンスのデータ件数が格納されているパラメータ名（デフォルト値: total)
 * data-table-name-hash: 検索条件のハッシュ値を格納するパラメータ名（デフォルト値: hash）
 *
 * 属性（thead内のth）
 * data-column-name: ソートするパラメータ名（ソート可能とする場合に付与します）
 * data-column-sort: ソート順（asc: 昇順、desc: 降順）
 *
 * 属性（tbody内のtr）
 * data-row-index: 行番号（0始まり）
 * data-bind: 行に割り当てられたデータのJSON
 * data-table-total: 検索条件に一致する件数（外部からは変更しないでください）
 * data-table-count: 現在のデータの取得件数（外部からは変更しないでください）
 * data-table-error: エラー発生時のメッセージ（外部からは変更しないでください）
 *
 * 属性（tbody内のtd）
 * data-column-name: 表示する対象となるパラメータ名
 * data-column-direct: 値をtextContentではなくinnerHTMLで設定します
 * data-column-default: 値がnullもしくは未設定の場合のデフォルト値
 * data-column-script: 値を整形する処理を定義し表示する文字列を戻り値とします（value: パラメータ値、object: 全体のオブジェクト）
 * data-column-suffix: 内容が存在するときに後ろに付与する文字列
 *
 * 属性（tfoot）
 * data-foot-content: 行がある場合に表示する文字列
 * data-foot-empty: 行がない場合に表示する文字列
 * data-foot-error: エラーが発生している場合に表示する文字列
 * data-bind: total（全件数）、count（現在の取得件数）、top（表示中の行の中で一番上にある行の番号）、bottom（表示中の行の中で一番下にある行の番号）、error（エラーメッセージ）が設定されます
 *
 * イベント
 * fetch: 通信が完了した場合（detailはrequestとresponseのPromise）
 * add-row: 行が追加された際に発生します（detailはrow: trエレメント、object: 追加された行に割り当てられたデータ）
 */
class HaoriTable {
  /** アトリビュート変更オブザーバ */
  static attributeObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      let element = <HTMLElement>mutation.target;
      if (!Haori.hasClosestAttribute(element, 'data-table-bound', () => true)) {
        return;
      }
      switch (mutation.attributeName) {
        case 'data-table-fetch':
        case 'data-table-param':
          if (element.tagName == 'TABLE') {
            HaoriTable.fetch(<HTMLTableElement>element);
          }
          break;
        case 'data-foot-content':
        case 'data-foot-error':
        case 'data-foot-empty':
          if (element.tagName == 'TFOOT') {
            HaoriTable.updateStatus(<HTMLTableElement>element.closest('table'));
          }
          break;
      }
    });
  });

  /**
   * parentエレメント以下のテーブルエレメントにイベントを関連付け最初のフェッチを行います。
   */
  static bind(parent: HTMLElement) {
    parent.querySelectorAll('table[data-table-fetch]').forEach((element) => {
      let table = <HTMLTableElement>element;
      if (table.getAttribute('data-table-bound') != null || table.tHead == null) {
        return;
      }
      if (table.tFoot) {
        let bind: any = {};
        bind.total = 0;
        bind.count = null;
        bind.top = null;
        bind.bottom = null;
        bind.hash = null;
        bind.error = null;
        table.tFoot.setAttribute('data-bind', JSON.stringify(bind));
      }
      table.setAttribute('data-table-bound', '');
      table.tHead.querySelectorAll('th, td').forEach((cell) => {
        cell.addEventListener('click', (event) => {
          if ((<Element>event.target).getAttribute('data-column-name') == null || table.tHead == null) {
            return;
          }
          table.tHead.querySelectorAll('th[data-column-sort], td[data-column-sort]').forEach((current) => {
            if (current != null && current != cell) {
              current.removeAttribute('data-column-sort');
            }
          });
          let name = cell.getAttribute('data-column-name');
          if (name == null) {
            return;
          }
          let sort = cell.getAttribute('data-column-sort');
          if (sort == null) {
            cell.setAttribute('data-column-sort', 'asc');
          } else if (sort == 'asc') {
            cell.setAttribute('data-column-sort', 'desc');
          } else if (sort == 'desc') {
            cell.removeAttribute('data-column-sort');
          }
          this.fetch(table);
        });
      });
      let lastRow = table.tBodies[0].querySelector('tr:last-child');
      (<HTMLElement>lastRow).style.display = 'none';
      lastRow?.setAttribute('data-bind-invalid', '');
      this.fetch(table);
    });
  }

  /**
   * 新しい検索条件でテーブルのデータを再構築します。
   *
   * @param table テーブルエレメント
   */
  static fetch(table: HTMLTableElement) {
    table.removeAttribute('data-table-total');
    table.removeAttribute('data-table-count');

    if (table.tFoot) {
      let bind = table.tFoot.hasAttribute('data-bind') ? JSON.parse(<string>table.tFoot.getAttribute('data-bind')) : {};
      bind.total = 0;
      bind.count = null;
      bind.top = null;
      bind.bottom = null;
      bind.hash = null;
      bind.error = null;
      table.tFoot.setAttribute('data-bind', JSON.stringify(bind));
    }
    let tbody = table.tBodies[0];
    let lastRow = tbody.querySelector('tr:last-child');
    if (lastRow == null) {
      return;
    }
    lastRow.remove();
    tbody.innerHTML = '';
    tbody.append(lastRow);
    this.fetchNext(table);
  }

  /**
   * 必要に応じて次のリストを取得します。
   *
   * @param table テーブルエレメント
   */
  static fetchNext(table: HTMLTableElement) {
    if (table.getAttribute('data-table-fetch') == null) {
      return;
    }

    let bind = table.tFoot?.hasAttribute('data-bind') ? JSON.parse(<string>table.tFoot.getAttribute('data-bind')) : {};
    let total = bind.total;
    let count = bind.count;
    if (count != null && count >= total) {
      setTimeout(() => {
        this.updateStatus(table);
      });
      setTimeout(() => {
        this.updateStatus(table);
      }, 500); // ダイアログの fade に対応するためディレイする
      return;
    }
    if (count == null) {
      count = 0;
    }
    let hash = bind.hash;

    let lastRow = table.tBodies[0].querySelector('tr:nth-last-child(2)');
    if (lastRow != null) {
      let lastRowRect = lastRow.getBoundingClientRect();
      if (table.tFoot) {
        let tFootRect = table.tFoot.getBoundingClientRect();
        if (tFootRect.top < lastRowRect.top - lastRowRect.height * 40) {
          // 2回分先に読み込まれているためスキップする
          setTimeout(() => {
            this.updateStatus(table);
          });
          setTimeout(() => {
            this.updateStatus(table);
          }, 500); // ダイアログの fade に対応するためディレイする
          return;
        }
      }
    }

    if (table.getAttribute('data-table-busy') != null) {
      return;
    }
    table.setAttribute('data-table-busy', '');
    table.removeAttribute('data-error');

    let offsetName = table.getAttribute('data-table-name-offset') || 'offset';
    let limitName = table.getAttribute('data-table-name-limit') || 'limit';
    let listName = table.getAttribute('data-table-name-list') || 'list';
    let totalName = table.getAttribute('data-table-name-total') || 'total';
    let hashName = table.getAttribute('data-table-name-hash') || 'hash';

    let url = <string>table.getAttribute('data-table-fetch');
    let paramPoint = url.indexOf('?');
    let params = new URLSearchParams(paramPoint > 0 ? url.substring(paramPoint + 1) : undefined);
    let fields: string[] = params.has('fields') ? params.get('fields')?.split(',')! : [];
    table.tBodies[0].querySelectorAll('tr:last-child td').forEach((td) => {
      Haori.hasAttribute(td, 'data-column-name', (element, value) => {
        if (value == null || value.length == 0 || fields.indexOf(value) >= 0) {
          return;
        }
        fields.push(value);
      });
    });
    params.set('fields', fields.join(','));
    params.set(offsetName, count.toString());
    params.set(limitName, '20');
    if (hash) {
      params.set(hashName, hash);
    }
    if (table.tHead) {
      let sort = table.tHead.querySelector(
        'th[data-column-name][data-column-sort], td[data-column-name][data-column-sort]'
      );
      if (sort != null) {
        let sortName = sort.getAttribute('data-column-name');
        params.set('sort', (sort.getAttribute('data-column-sort') == 'desc' ? '-' : '') + sortName);
      }
    }
    let paramsString = params.toString();
    if (table.getAttribute('data-table-param') != null) {
      let dataParam = <HashObject>JSON.parse(table.getAttribute('data-table-param') || '{}');
      if (paramsString.length > 0) {
        paramsString += '&';
      }
      paramsString += HaoriForm.getQueryParameterFromObject(dataParam);
    }
    if (paramsString.length > 0) {
      if (paramPoint > 0) {
        url = url.substring(0, paramPoint) + '?' + paramsString;
      } else {
        url += '?' + paramsString;
      }
    }

    Haori.fetch(
      [table],
      url,
      {},
      (response, data) => {
        if (response.ok) {
          let params: HashObject = {};
          for (let key in data) {
            if (key == listName || key == totalName || key == hashName) {
              continue;
            }
            params[key] = data[key];
          }
          this.appendRows(table, data[listName], data[totalName], data[hashName], params);
        } else {
          bind.error = Haori.options.errorMessageConverter(url, <Request>{}, response, data);
          if (table.tFoot) {
            table.tFoot.setAttribute('data-bind', JSON.stringify(bind));
          }
          table.setAttribute('data-table-error', bind.error);
        }
        table.removeAttribute('data-table-busy');
        if (response.ok) {
          this.fetchNext(table);
        } else {
          setTimeout(() => {
            this.updateStatus(table);
          });
          setTimeout(() => {
            this.updateStatus(table);
          }, 500); // イベントの実行結果に対応するためディレイする
        }
      },
      (reason) => {
        bind.error = reason;
        if (table.tFoot) {
          table.tFoot.setAttribute('data-bind', JSON.stringify(bind));
        }
        table.setAttribute('data-table-error', reason);
        table.removeAttribute('data-table-busy');
      }
    );
  }

  /**
   * テーブルに行を追加します。
   *
   * @param table 対象テーブル
   * @param list 追加するデータのリスト
   * @param total 全部の件数
   * @param hash 検索ハッシュ
   * @param params 追加パラメータ
   */
  static appendRows(table: HTMLTableElement, list: object[], total: number, hash: string | null = null, params: HashObject | null = null) {
    let bind = table.tFoot?.hasAttribute('data-bind') ? JSON.parse(<string>table.tFoot.getAttribute('data-bind')) : {};
    if (params != null) {
      for (let key in params) {
        bind[key] = params[key];
      }
    }
    bind.total = total;
    bind.hash = hash;
    let count = parseInt(table.getAttribute('data-table-count') || '0');
    let tbody = table.tBodies[0];
    let lastRow = tbody.querySelector('tr:last-child');
    if (lastRow == null) {
      console.error('サンプル行が設定されていません');
      return;
    }
    let samples = lastRow.querySelectorAll('th, td');
    list.forEach((object, index) => {
      let tr = document.createElement('tr');
      (<HTMLElement>lastRow).classList.forEach((clazz) => {
        tr.classList.add(clazz);
      });
      tr.setAttribute('data-row-index', (count + index).toString());
      samples.forEach((sample) => {
        let td = document.createElement('td');
        let name = sample.getAttribute('data-column-name');
        let value: any = null;
        if (name == null) {
          td.innerHTML = sample.innerHTML;
        } else {
          value = Haori.getValue(object, name);
          if ((value === null || value === undefined) && sample.getAttribute('data-column-default') != null) {
            value = sample.getAttribute('data-column-default');
          }
          if (value == null) {
            value = '';
          }
          let script = sample.getAttribute('data-column-script');
          if (script != null && value != '') {
            try {
              value = Haori.executeScript(
                {
                  object: object,
                  value: value,
                },
                script,
                tr
              );
            } catch (error) {
              value = script;
            }
          }
          if (sample.getAttribute('data-column-suffix') != null && value != '') {
            value = value + <string>sample.getAttribute('data-column-suffix');
          }
          if (sample.getAttribute('data-column-direct') != null) {
            td.innerHTML = sample.innerHTML + (value != null ? value.toString() : '');
          } else {
            td.innerHTML = sample.innerHTML;
            if (value != null) {
              td.appendChild(document.createTextNode(value.toString()));
            }
          }
        }
        if (sample.getAttribute('class') != null) {
          td.setAttribute('class', <string>sample.getAttribute('class'));
        }
        tr.appendChild(td);
      });
      (<Element>lastRow).before(tr);
      tr.setAttribute('data-bind', JSON.stringify(object));
      table.dispatchEvent(
        new CustomEvent('add-row', {
          bubbles: true,
          cancelable: true,
          composed: true,
          detail: {
            row: tr,
            object: object,
          },
        })
      );
    });
    bind.count = count + list.length;
    table.setAttribute('data-table-count', (count + list.length).toString());
    table.setAttribute('data-table-total', total.toString());
    if (table.tFoot) {
      table.tFoot.setAttribute('data-bind', JSON.stringify(bind));
    }
  }

  /**
   * スクロール状態とフッタの表示を更新します。
   *
   * @param table テーブルエレメント
   */
  static updateStatus(table: HTMLTableElement) {
    let bind = table.tFoot?.hasAttribute('data-bind') ? JSON.parse(<string>table.tFoot.getAttribute('data-bind')) : {};
    if (bind.count > 0) {
      if (table.tHead) {
        let tHeadRect = table.tHead.getBoundingClientRect();
        let topCell = document.elementFromPoint(tHeadRect.left + 1, tHeadRect.bottom + 1);
        if (topCell != null) {
          let topRow = topCell.closest('tr');
          if (topRow != null && topRow.getAttribute('data-row-index') != null) {
            bind.top = parseInt(<string>topRow.getAttribute('data-row-index')) + 1;
          }
        }
      }
      if (table.tFoot) {
        let tFootRect = table.tFoot.getBoundingClientRect();
        let bottomCell = document.elementFromPoint(tFootRect.left + 1, tFootRect.top - 1);
        if (bottomCell != null) {
          let bottomRow = bottomCell.closest('tr');
          if (bottomRow != null && bottomRow.getAttribute('data-row-index') != null) {
            bind.bottom = parseInt(<string>bottomRow.getAttribute('data-row-index')) + 1;
          }
        }
      }
    }

    if (table.tFoot) {
      if (table.tFoot.getElementsByTagName('tr').length == 0) {
        let tr = document.createElement('tr');
        let td = document.createElement('td');
        if (table.tHead) {
          td.setAttribute('colspan', table.tHead.querySelectorAll('th, td').length.toString());
        }
        tr.appendChild(td);
        table.tFoot.appendChild(tr);
      }
      let content: string | null = null;
      if (bind.error && table.tFoot.hasAttribute('data-foot-error')) {
        content = table.tFoot.getAttribute('data-foot-error');
      } else if (bind.count == 0 && table.tFoot.hasAttribute('data-foot-empty')) {
        content = table.tFoot.getAttribute('data-foot-empty');
      } else if (table.tFoot.hasAttribute('data-foot-content')) {
        content = table.tFoot.getAttribute('data-foot-content');
      }
      if (content != null) {
        let td = table.tFoot.querySelector('th, td');
        if (td != null) {
          td.textContent = content;
        }
      }
      table.tFoot.setAttribute('data-bind', JSON.stringify(bind));
    }
  }
}

window.addEventListener('load', (event) => {
  HaoriTable.bind(document.body);
});

document.body.addEventListener('load', (event) => {
  HaoriTable.bind(<HTMLElement>event.target);
});

let haoriTableBusy = false;
window.addEventListener(
  'scroll',
  (event) => {
    if (haoriTableBusy) {
      return;
    }
    haoriTableBusy = true;
    requestAnimationFrame(() => {
      Array.prototype.forEach.call(
        (<HTMLElement>event.target).querySelectorAll('table[data-table-fetch]'),
        (table: HTMLTableElement) => {
          HaoriTable.fetchNext(table);
        }
      );
      haoriTableBusy = false;
    });
  },
  true
);

HaoriTable.attributeObserver.observe(document.body, {
  attributes: true,
  subtree: true,
  attributeOldValue: true,
  attributeFilter: ['data-table-fetch', 'data-table-param', 'data-foot-content', 'data-foot-error', 'data-foot-empty'],
});
